import { SIZE_LABELS, SIZE_OPTIONS, type SizeOption } from "../../catalog/taxonomy";
import type { PaletteColor } from "../../catalog/colors";
import {
  discountPercent,
  parseStock,
  pricesFor,
  type DetailProblem,
  type GlassesDetails,
} from "../../import/glassesDetails";

/**
 * What the shop needs to know about the product, once the folder is settled.
 *
 * The questions are asked in the order a merchant thinks about them — what it
 * is called, what it costs, how many there are — rather than in the order the
 * product contract stores them. Stock is asked per detected colour, beside that
 * colour's name and swatch, because "how many do you have" is a question about
 * a specific frame in a specific colour and nothing else.
 *
 * Problems are shown against the box they belong to and only after the
 * operator has been in that box, so a form that has simply not been filled in
 * yet does not greet them in red.
 */

/** One detected colour, already resolved to a palette colour by the review. */
export interface ResolvedColor {
  folderName: string;
  color: PaletteColor;
}

function problemFor(
  problems: readonly DetailProblem[],
  field: string,
  colorFolder?: string,
): DetailProblem | undefined {
  return problems.find(
    (p) => p.field === field && (colorFolder === undefined || p.colorFolder === colorFolder),
  );
}

function FieldError({ problem, show }: { problem?: DetailProblem; show: boolean }) {
  if (!problem || !show) return null;
  return (
    <p className="field-error" role="alert">
      {problem.message}
    </p>
  );
}

export function ProductDetailsStep({
  details,
  colors,
  problems,
  touched,
  onTouch,
  onChange,
}: {
  details: GlassesDetails;
  colors: readonly ResolvedColor[];
  problems: readonly DetailProblem[];
  /** Fields the operator has already left, so errors appear at the right time. */
  touched: ReadonlySet<string>;
  onTouch: (field: string) => void;
  onChange: (next: GlassesDetails) => void;
}) {
  const set = <K extends keyof GlassesDetails>(key: K, value: GlassesDetails[K]) =>
    onChange({ ...details, [key]: value });

  const chooseSize = (size: SizeOption) => {
    onTouch("size");
    set("size", size);
  };

  const { base, sale } = pricesFor(details.pricing);
  const discount = details.pricing.onSale ? discountPercent(base, sale) : null;

  return (
    <section className="step details" aria-label="Product information">
      <label className="field">
        <span className="field__label">Product name</span>
        <span className="field__hint">
          Suggested from the folder name. Change it to whatever customers should see.
        </span>
        <input
          className="input"
          value={details.name}
          onChange={(event) => set("name", event.target.value)}
          onBlur={() => onTouch("name")}
          data-testid="product-name"
        />
        <FieldError problem={problemFor(problems, "name")} show={touched.has("name")} />
      </label>

      <label className="field">
        <span className="field__label">Product code</span>
        <span className="field__hint">
          Your shop's own code for this frame. You can search for the product by it later.
        </span>
        <input
          className="input"
          value={details.productCode}
          onChange={(event) => set("productCode", event.target.value)}
          onBlur={() => onTouch("productCode")}
          spellCheck={false}
          data-testid="product-code"
        />
        <FieldError
          problem={problemFor(problems, "productCode")}
          show={touched.has("productCode")}
        />
      </label>

      <label className="field">
        <span className="field__label">Description</span>
        <span className="field__hint">Shown on the product page in the app.</span>
        <textarea
          className="input input--area"
          rows={4}
          value={details.description}
          onChange={(event) => set("description", event.target.value)}
          onBlur={() => onTouch("description")}
          data-testid="product-description"
        />
        <FieldError
          problem={problemFor(problems, "description")}
          show={touched.has("description")}
        />
      </label>

      <fieldset className="field">
        <legend className="field__label">Size</legend>
        <p className="field__hint">
          The one size this frame is. If you also sell it in another size, add that
          as its own product — you will be offered to group them.
        </p>
        <div className="choiceRow" role="radiogroup" aria-label="Frame size">
          {SIZE_OPTIONS.map((size) => (
            <label
              key={size}
              className={details.size === size ? "choice is-selected" : "choice"}
            >
              <input
                type="radio"
                name="frame-size"
                value={size}
                checked={details.size === size}
                onChange={() => chooseSize(size)}
              />
              <span className="choice__label">{SIZE_LABELS[size]}</span>
            </label>
          ))}
        </div>
        <FieldError problem={problemFor(problems, "size")} show={touched.has("size")} />
      </fieldset>

      <fieldset className="field">
        <legend className="field__label">Stock</legend>
        <p className="field__hint">
          How many you have of each colour. Enter 0 for a colour you have none of.
        </p>
        <div className="stockRows">
          {colors.map(({ folderName, color }) => {
            const typed = details.stock[folderName] ?? "";
            const problem = problemFor(problems, "stock", folderName);
            const count = parseStock(typed);
            return (
              <div className="stockRow" key={folderName}>
                <span
                  className="swatch__dot"
                  style={{ backgroundColor: color.swatch_hex }}
                  aria-hidden="true"
                />
                <span className="stockRow__name">{color.name}</span>
                <input
                  className="input input--num"
                  inputMode="numeric"
                  value={typed}
                  aria-label={`Stock for ${color.name}`}
                  onChange={(event) =>
                    set("stock", { ...details.stock, [folderName]: event.target.value })
                  }
                  onBlur={() => onTouch(`stock:${folderName}`)}
                  data-testid={`stock-${folderName}`}
                />
                {count === 0 ? <span className="tag tag--warn">Out of stock</span> : null}
                <FieldError problem={problem} show={touched.has(`stock:${folderName}`)} />
              </div>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="field">
        <legend className="field__label">Price</legend>

        <label className="choice choice--switch">
          <input
            type="checkbox"
            checked={details.pricing.onSale}
            onChange={(event) =>
              set("pricing", { ...details.pricing, onSale: event.target.checked })
            }
            data-testid="on-sale"
          />
          <span className="choice__label">This product is on sale</span>
        </label>

        {!details.pricing.onSale ? (
          <label className="field field--inline">
            <span className="field__label">Price</span>
            <input
              className="input input--money"
              inputMode="decimal"
              value={details.pricing.regular}
              onChange={(event) =>
                set("pricing", { ...details.pricing, regular: event.target.value })
              }
              onBlur={() => onTouch("regular")}
              data-testid="regular-price"
            />
            <FieldError
              problem={problemFor(problems, "regular")}
              show={touched.has("regular")}
            />
          </label>
        ) : (
          <div className="saleRow">
            <label className="field field--inline">
              <span className="field__label">Original price</span>
              <input
                className="input input--money"
                inputMode="decimal"
                value={details.pricing.original}
                onChange={(event) =>
                  set("pricing", { ...details.pricing, original: event.target.value })
                }
                onBlur={() => onTouch("original")}
                data-testid="original-price"
              />
              <FieldError
                problem={problemFor(problems, "original")}
                show={touched.has("original")}
              />
            </label>

            <label className="field field--inline">
              <span className="field__label">Sale price</span>
              <input
                className="input input--money"
                inputMode="decimal"
                value={details.pricing.sale}
                onChange={(event) =>
                  set("pricing", { ...details.pricing, sale: event.target.value })
                }
                onBlur={() => onTouch("sale")}
                data-testid="sale-price"
              />
              <FieldError
                problem={problemFor(problems, "sale")}
                show={touched.has("sale")}
              />
            </label>

            {/* Calculated, never entered: the percentage is a view of the two
                prices, so it cannot drift away from them. */}
            <p className="saleRow__discount" data-testid="discount">
              {discount === null
                ? "Enter both prices to see the discount."
                : `Customers save ${discount}%`}
            </p>
          </div>
        )}
      </fieldset>
    </section>
  );
}
